// src/pages/Puestos.jsx
import { useState, useEffect, useRef } from 'react';
import { getPosts, savePost, deletePost } from '../data/store';

const BLANK = {
  name:'',
  location:'',
  address:'',
  shifts:['08:00/16:00'],
  armed:false,
  notes:'',
  coverage: '24/7',
  days: [1,2,3,4,5,6,0],
  guardsPerShift: 1,
  lat: '',
  lng: '',
  radioMetros: 100
};

const DEFAULT_SHIFT_FORM = {
  startHour:'8', startMin:'00', startAmpm:'AM',
  endHour:'4', endMin:'00', endAmpm:'PM'
};

const PR_CENTER = { lat: 18.2208, lng: -66.5901 };

const S = {
  page: { color:'#fff' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' },
  title: { fontSize:'1.6rem', fontWeight:800, color:'#F5C518', margin:0 },
  btn: { background:'#F5C518', color:'#000', border:'none', borderRadius:8, padding:'10px 20px', fontWeight:700, cursor:'pointer', fontSize:14 },
  btnRed: { background:'rgba(220,38,38,0.15)', color:'#f87171', border:'1px solid rgba(220,38,38,0.3)', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 },
  btnEdit: { background:'rgba(245,197,24,0.15)', color:'#F5C518', border:'1px solid rgba(245,197,24,0.3)', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 },
  btnBlue: { background:'rgba(30,95,168,0.15)', color:'#5b9bd5', border:'1px solid rgba(30,95,168,0.3)', borderRadius:6, padding:'8px 12px', cursor:'pointer', fontSize:12, fontWeight:600 },
  card: { background:'#1a1a1a', border:'1px solid #222', borderRadius:12, padding:'1.25rem', marginBottom:12 },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  modal: { background:'#1a1a1a', border:'1px solid #333', borderRadius:16, padding:'2rem', width:'100%', maxWidth:540, maxHeight:'90vh', overflowY:'auto' },
  label: { display:'block', color:'#888', fontSize:12, marginBottom:4, marginTop:16 },
  input: { width:'100%', padding:'10px 12px', background:'#0d0d0d', border:'1px solid #333', borderRadius:8, color:'#fff', fontSize:14, boxSizing:'border-box' },
};

function to24h(hour, min, ampm) {
  if (!hour) return '';
  let h = parseInt(hour, 10);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return String(h).padStart(2,'0') + ':' + (min || '00');
}

// Siempre muestra minutos: "10:00 PM - 6:00 AM"
function formatShift12h(shift) {
  if (!shift) return '';
  const sep = shift.includes('/') ? '/' : '-';
  const [start, end] = shift.split(sep);
  const convert = (time) => {
    if (!time) return '';
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    m = m ? String(m).padStart(2,'0') : '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + m + ' ' + ampm;
  };
  return convert(start) + ' - ' + convert(end);
}

function hasCoords(f) {
  return f.lat !== '' && f.lat !== null && f.lat !== undefined && !Number.isNaN(Number(f.lat)) &&
         f.lng !== '' && f.lng !== null && f.lng !== undefined && !Number.isNaN(Number(f.lng));
}

export default function Puestos() {
  const [posts, setPosts] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [shiftForm, setShiftForm] = useState({ ...DEFAULT_SHIFT_FORM });
  const [shiftError, setShiftError] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeMsg, setGeocodeMsg] = useState('');
  const [leafletReady, setLeafletReady] = useState(false);
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  const reload = async () => {
    try { const data = await getPosts(); setPosts(data); }
    catch (error) { console.error('Error cargando puestos:', error); }
  };

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.body.appendChild(script);
  }, []);

  // Inicializa el mapa cuando el modal está abierto
  useEffect(() => {
    if (!modal || !leafletReady || !mapRef.current || mapObjRef.current) return;
    const L = window.L;
    const start = hasCoords(form) ? { lat:Number(form.lat), lng:Number(form.lng) } : PR_CENTER;
    const map = L.map(mapRef.current, { attributionControl:false, scrollWheelZoom:false, doubleClickZoom:false }).setView([start.lat, start.lng], hasCoords(form) ? 16 : 9);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom:19 }).addTo(map);
    map.on('click', (ev) => {
      setForm((f) => ({ ...f, lat: ev.latlng.lat, lng: ev.latlng.lng }));
    });
    mapObjRef.current = map;
    setTimeout(() => map.invalidateSize(), 150);
  }, [modal, leafletReady]);

  // Destruye el mapa al cerrar el modal
  useEffect(() => {
    if (!modal && mapObjRef.current) {
      mapObjRef.current.remove();
      mapObjRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    }
  }, [modal]);

  // Actualiza marcador + círculo cuando cambian lat/lng/radio
  useEffect(() => {
    const map = mapObjRef.current;
    if (!map || !window.L) return;
    const L = window.L;
    if (!hasCoords(form)) return;
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (!markerRef.current) {
      markerRef.current = L.marker([lat, lng], { draggable:true }).addTo(map);
      markerRef.current.on('dragend', (ev) => {
        const pos = ev.target.getLatLng();
        setForm((f) => ({ ...f, lat: pos.lat, lng: pos.lng }));
      });
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }
    if (circleRef.current) map.removeLayer(circleRef.current);
    circleRef.current = L.circle([lat, lng], { radius: Number(form.radioMetros) || 100, color:'#F5C518', fillOpacity:0.12 }).addTo(map);
    map.setView([lat, lng], map.getZoom() < 14 ? 16 : map.getZoom());
  }, [form.lat, form.lng, form.radioMetros]);

  const open = (post = null) => {
    if (post) {
      setForm({
        ...post,
        shifts: [...(post.shifts || ['08:00/16:00'])],
        coverage: post.coverage || '24/7',
        days: post.days || [1,2,3,4,5,6,0],
        guardsPerShift: post.guardsPerShift || 1,
        lat: post.lat ?? '',
        lng: post.lng ?? '',
        radioMetros: post.radio_metros ?? post.radioMetros ?? 100,
      });
    } else {
      setForm({ ...BLANK, shifts: ['08:00/16:00'] });
    }
    setShiftForm({ ...DEFAULT_SHIFT_FORM });
    setShiftError('');
    setGeocodeMsg('');
    setModal(true);
  };

  const close = () => { setModal(false); setShiftError(''); setGeocodeMsg(''); };

  const save = async () => {
    if (!form.name.trim()) { alert('El nombre del puesto es requerido'); return; }
    try {
      await savePost(form);
      await reload();
      close();
    } catch (err) {
      console.error('Error guardando puesto:', err);
      alert('Error al guardar: ' + (err.message || 'Error desconocido'));
    }
  };

  const del = async (id) => {
    if (window.confirm('¿Eliminar este puesto?')) { await deletePost(id); await reload(); }
  };

  const addShift = () => {
    const start = to24h(shiftForm.startHour, shiftForm.startMin, shiftForm.startAmpm);
    const end = to24h(shiftForm.endHour, shiftForm.endMin, shiftForm.endAmpm);
    if (!start || !end) { setShiftError('Debes completar la hora de entrada y salida.'); return; }
    const shift = start + '/' + end;
    if ((form.shifts || []).includes(shift)) { setShiftError('Ese turno ya existe en este puesto.'); return; }
    setShiftError('');
    setForm(f => ({ ...f, shifts: [...(f.shifts || []), shift] }));
  };

  const removeShift = (idx) => setForm(f => ({ ...f, shifts: f.shifts.filter((_,i) => i !== idx) }));

  const useMyLocation = () => {
    if (!navigator.geolocation) { alert('Este dispositivo no tiene GPS.'); return; }
    setGeocodeMsg('');
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude })),
      () => alert('No se pudo obtener tu ubicación actual.')
    );
  };

  const geocodeAddress = async () => {
    const query = [form.address, form.location, form.name].filter(Boolean).join(', ');
    if (!query.trim()) { alert('Escribe primero la dirección o localización del puesto.'); return; }
    setGeocoding(true); setGeocodeMsg('');
    try {
      const url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(query + ', Puerto Rico');
      const res = await fetch(url);
      const data = await res.json();
      if (data && data[0]) {
        setForm(f => ({ ...f, lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }));
        setGeocodeMsg('📍 Encontrado: ' + data[0].display_name);
      } else {
        alert('No se encontró esa dirección. Prueba con una dirección más específica, o marca el punto exacto haciendo clic en el mapa.');
      }
    } catch (err) {
      alert('Error buscando la dirección. Intenta de nuevo o marca el punto en el mapa.');
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Puestos</h1>
          <p style={{ color:'#666', margin:'4px 0 0', fontSize:14 }}>{posts.length} puestos registrados</p>
        </div>
        <button type="button" style={S.btn} onClick={() => open()}>+ Añadir puesto</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
        {posts.map(post => (
          <div key={post.id} style={S.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16, color:'#F5C518' }}>{post.name}</div>
                {post.location && <div style={{ color:'#888', fontSize:12, marginTop:2 }}>📍 {post.location}</div>}
                {post.address && <div style={{ color:'#666', fontSize:11, marginTop:2 }}>{post.address}</div>}
              </div>
              <span style={{ background: post.armed ? 'rgba(239,68,68,0.15)' : 'rgba(52,211,153,0.15)', border:'1px solid ' + (post.armed ? 'rgba(239,68,68,0.3)' : 'rgba(52,211,153,0.3)'), borderRadius:4, padding:'2px 8px', fontSize:11, color: post.armed ? '#f87171' : '#34d399', flexShrink:0 }}>
                {post.armed ? '🔫 Armado' : '🛡 Desarmado'}
              </span>
            </div>
            <div style={{ marginBottom:6 }}>
              <div style={{ color:'#666', fontSize:11, marginBottom:4 }}>
                COBERTURA: <span style={{ color:'#fbbf24' }}>{post.coverage === '24/7' ? '24/7' : 'Días específicos'}</span>
                {' · '}GUARDIAS/TURNO: <span style={{ color:'#fbbf24' }}>{post.guardsPerShift || 1}</span>
                {' · '}GEO-CERCA: <span style={{ color: (post.lat !== null && post.lat !== undefined && post.lat !== '') ? '#34d399' : '#f87171' }}>{(post.lat !== null && post.lat !== undefined && post.lat !== '') ? ('activa (' + (post.radio_metros || post.radioMetros || 100) + ' m)') : 'sin configurar'}</span>
              </div>
            </div>
            <div style={{ marginBottom:10 }}>
              <div style={{ color:'#666', fontSize:11, marginBottom:4 }}>TURNOS:</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {(post.shifts||[]).map((s,i) => (
                  <span key={i} style={{ background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:4, padding:'2px 8px', fontSize:12, color:'#93c5fd' }}>
                    {formatShift12h(s)}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" style={S.btnEdit} onClick={() => open(post)}>Editar</button>
              <button type="button" style={S.btnRed} onClick={() => del(post.id)}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {posts.length === 0 && (
        <div style={{ textAlign:'center', color:'#444', padding:'3rem' }}>No hay puestos. Añade el primero.</div>
      )}

      {modal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && close()}>
          <div style={S.modal}>
            <h2 style={{ color:'#F5C518', margin:'0 0 1rem', fontSize:18 }}>
              {form.id ? 'Editar' : 'Nuevo'} Puesto
            </h2>

            <label style={S.label}>Nombre del puesto *</label>
            <input style={S.input} value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="Ej. CSM-CAGUAS" />

            <label style={S.label}>Localización</label>
            <input style={S.input} value={form.location||''} onChange={e => setForm({...form,location:e.target.value})} placeholder="Ej. Caguas" />

            <label style={S.label}>Dirección</label>
            <input style={S.input} value={form.address||''} onChange={e => setForm({...form,address:e.target.value})} placeholder="Dirección completa" />

            <label style={S.label}>Geo-cerca (GPS del puesto) — necesaria para restringir el Clock In al lugar correcto</label>
            <div style={{ display:'flex', gap:8 }}>
              <input style={{...S.input, flex:1}} type="number" step="0.000001" value={form.lat ?? ''} onChange={e => setForm({...form,lat:e.target.value})} placeholder="Latitud" />
              <input style={{...S.input, flex:1}} type="number" step="0.000001" value={form.lng ?? ''} onChange={e => setForm({...form,lng:e.target.value})} placeholder="Longitud" />
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
              <button type="button" style={S.btnBlue} onClick={geocodeAddress} disabled={geocoding}>
                {geocoding ? 'Buscando...' : '🔍 Buscar por dirección'}
              </button>
              <button type="button" style={S.btnBlue} onClick={useMyLocation}>📍 Usar mi ubicación actual</button>
            </div>
            {geocodeMsg && <div style={{ fontSize:11, color:'#8aa3c2', marginTop:6 }}>{geocodeMsg}</div>}

            <div ref={mapRef} style={{ width:'100%', height:200, borderRadius:10, overflow:'hidden', border:'1px solid #333', marginTop:10, background:'#0a1628' }} />
            <div style={{ fontSize:11, color:'#666', marginTop:6 }}>
              Tip: haz clic en el mapa o arrastra el marcador amarillo para ajustar el punto exacto del puesto. El círculo muestra el radio permitido para hacer Clock In. (El zoom con la rueda del mouse está desactivado para no interferir al hacer scroll en este formulario — usa los botones +/-).
            </div>

            <label style={S.label}>Radio permitido (metros)</label>
            <select style={S.input} value={form.radioMetros || 100} onChange={e => setForm({...form,radioMetros:Number(e.target.value)})}>
                        <option value="5">5 metros</option>
                        <option value="10">10 metros</option>
                        <option value="15">15 metros</option>
                        <option value="20">20 metros</option>
                        <option value="30">30 metros</option>
                        <option value="60">60 metros</option>
                        <option value="65">65 metros</option>
            </select>

            <label style={S.label}>Tipo</label>
            <select style={S.input} value={form.armed?'armed':'unarmed'} onChange={e => setForm({...form,armed:e.target.value==='armed'})}>
              <option value="unarmed">Desarmado</option>
              <option value="armed">Armado</option>
            </select>

            <label style={S.label}>Cobertura del puesto</label>
            <select style={S.input} value={form.coverage||'24/7'} onChange={e => setForm({...form,coverage:e.target.value})}>
              <option value="24/7">24/7 - todos los días</option>
              <option value="weekly">Días específicos de la semana</option>
            </select>

            {form.coverage === '24/7' && (
              <div style={{ background:'rgba(245,197,24,0.07)', border:'1px solid rgba(245,197,24,0.2)', borderRadius:8, padding:'10px 14px', marginTop:10, fontSize:12, color:'#aaa' }}>
                ℹ️ En modo 24/7, Vacantes verificará que los turnos cubran las 24 horas cada día sin huecos.
              </div>
            )}

            {form.coverage === 'weekly' && (
              <>
                <label style={S.label}>Días de trabajo</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map((d,i) => (
                    <button type="button" key={i}
                      onClick={() => {
                        const exists = form.days?.includes(i);
                        setForm({ ...form, days: exists ? form.days.filter(x=>x!==i) : [...(form.days||[]),i] });
                      }}
                      style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #333', background:form.days?.includes(i)?'#F5C518':'#0d0d0d', color:form.days?.includes(i)?'#000':'#fff', cursor:'pointer' }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </>
            )}

            <label style={S.label}>Guardias necesarios por turno</label>
            <input type="number" min="1" style={S.input} value={form.guardsPerShift||1} onChange={e => setForm({...form,guardsPerShift:parseInt(e.target.value)||1})} />

            <label style={S.label}>Turnos disponibles</label>
            {(form.shifts||[]).length===0 && (
              <div style={{ color:'#555', fontSize:12, marginBottom:6, fontStyle:'italic' }}>No hay turnos. Añade al menos uno abajo.</div>
            )}
            {(form.shifts||[]).map((s,i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6, background:'rgba(96,165,250,0.06)', borderRadius:6, padding:'6px 10px' }}>
                <span style={{ flex:1, color:'#93c5fd', fontSize:13 }}>{formatShift12h(s)}</span>
                <button type="button" onClick={() => removeShift(i)} style={{ background:'none', border:'none', color:'#f87171', cursor:'pointer', fontSize:16, lineHeight:1 }}>✕</button>
              </div>
            ))}

            <div style={{ marginTop:10, background:'#0d0d0d', border:'1px solid #333', borderRadius:8, padding:'12px' }}>
              <div style={{ color:'#888', fontSize:11, marginBottom:8, fontWeight:600 }}>AÑADIR TURNO</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'flex-end' }}>
                <div>
                  <div style={{ color:'#666', fontSize:10, marginBottom:3 }}>Entrada</div>
                  <div style={{ display:'flex', gap:4 }}>
                    <input type="number" min="1" max="12" value={shiftForm.startHour} onChange={e=>setShiftForm({...shiftForm,startHour:e.target.value})} style={{ ...S.input, width:52, padding:'8px 6px' }} />
                    <select value={shiftForm.startMin} onChange={e=>setShiftForm({...shiftForm,startMin:e.target.value})} style={{ ...S.input, width:68, padding:'8px 4px' }}>
                      <option value="00">:00</option><option value="15">:15</option><option value="30">:30</option><option value="45">:45</option>
                    </select>
                    <select value={shiftForm.startAmpm} onChange={e=>setShiftForm({...shiftForm,startAmpm:e.target.value})} style={{ ...S.input, width:72, padding:'8px 4px' }}>
                      <option>AM</option><option>PM</option>
                    </select>
                  </div>
                </div>
                <span style={{ color:'#555', paddingBottom:8 }}>→</span>
                <div>
                  <div style={{ color:'#666', fontSize:10, marginBottom:3 }}>Salida</div>
                  <div style={{ display:'flex', gap:4 }}>
                    <input type="number" min="1" max="12" value={shiftForm.endHour} onChange={e=>setShiftForm({...shiftForm,endHour:e.target.value})} style={{ ...S.input, width:52, padding:'8px 6px' }} />
                    <select value={shiftForm.endMin} onChange={e=>setShiftForm({...shiftForm,endMin:e.target.value})} style={{ ...S.input, width:68, padding:'8px 4px' }}>
                      <option value="00">:00</option><option value="15">:15</option><option value="30">:30</option><option value="45">:45</option>
                    </select>
                    <select value={shiftForm.endAmpm} onChange={e=>setShiftForm({...shiftForm,endAmpm:e.target.value})} style={{ ...S.input, width:72, padding:'8px 4px' }}>
                      <option>AM</option><option>PM</option>
                    </select>
                  </div>
                </div>
                <button type="button" onClick={addShift} style={{ ...S.btn, padding:'8px 16px' }}>+ Añadir</button>
              </div>
              {shiftError && <div style={{ color:'#f87171', fontSize:12, marginTop:6 }}>⚠️ {shiftError}</div>}
            </div>

            <label style={S.label}>Notas</label>
            <textarea style={{ ...S.input, height:80, resize:'vertical' }} value={form.notes||''} onChange={e=>setForm({...form,notes:e.target.value})} />

            <div style={{ display:'flex', gap:10, marginTop:24, paddingTop:16, borderTop:'1px solid #222' }}>
              <button type="button" style={{ ...S.btn, flex:1, fontSize:15 }} onClick={save}>💾 Guardar</button>
              <button type="button" style={{ background:'rgba(220,38,38,0.15)', color:'#f87171', border:'1px solid rgba(220,38,38,0.3)', borderRadius:8, padding:'10px', flex:1, cursor:'pointer', fontWeight:600 }} onClick={close}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
